import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Armchair, ArrowLeft, Maximize2, RefreshCw } from "lucide-react";

const STATUS = {
  active: { ring: "#E2B857", label: "In Session", chip: "bg-gold-muted text-gold" },
  available: { ring: "#10B981", label: "Available", chip: "bg-emerald-500/15 text-emerald-400" },
  out_of_service: { ring: "#EF4444", label: "Out of Service", chip: "bg-destructive/15 text-destructive" },
  maintenance: { ring: "#F59E0B", label: "Maintenance", chip: "bg-amber-500/15 text-amber-400" },
};

export default function Board() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [chairs, setChairs] = useState([]);
  const [, tick] = useState(0);

  const load = useCallback(() => {
    api.get("/staff/board").then((r) => setChairs(r.data.chairs)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 10000);
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => { clearInterval(iv); clearInterval(t); };
  }, [load]);

  const fs = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen?.();
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="flex items-center justify-between border-b border-border px-5 py-3">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => nav(user?.role === "staff" ? "/staff" : "/admin")} className="gap-1.5 gold-border text-gold" data-testid="board-back-btn">
            <ArrowLeft size={15} /> Back
          </Button>
          <Logo size="sm" subtitle={false} />
        </div>
        <div className="hidden sm:block overline-label">Live Chair Board</div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} className="gap-1.5 gold-border text-gold"><RefreshCw size={15} /></Button>
          <Button variant="outline" size="sm" onClick={fs} className="gap-1.5 gold-border text-gold" data-testid="fullscreen-btn"><Maximize2 size={15} /> Fullscreen</Button>
        </div>
      </header>

      <div className="grid gap-6 p-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {chairs.map((c) => {
          const meta = STATUS[c.status] || STATUS.available;
          const s = c.session;
          let mm = "00", ss = "00", pct = 0, done = false;
          if (s) {
            const remaining = Math.max(0, Math.floor((new Date(s.ends_at) - Date.now()) / 1000));
            const total = s.duration_minutes * 60;
            pct = Math.max(0, Math.min(100, (remaining / total) * 100));
            mm = String(Math.floor(remaining / 60)).padStart(2, "0");
            ss = String(remaining % 60).padStart(2, "0");
            done = remaining <= 0;
          }
          return (
            <div key={c.id} className="rounded-3xl bg-card p-6 gold-border" data-testid={`board-chair-${c.slug}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-xl font-bold gold-text">{c.slug}</span>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${meta.chip}`}>{done ? "Finishing" : meta.label}</span>
              </div>
              <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><Armchair size={12} />{c.location_name}</div>

              <div className="relative mx-auto mt-6 flex h-44 w-44 items-center justify-center">
                <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="45" fill="none" stroke="#27272A" strokeWidth="5" />
                  {s && <circle cx="50" cy="50" r="45" fill="none" stroke={done ? "#10B981" : meta.ring} strokeWidth="5"
                    strokeDasharray={`${2 * Math.PI * 45}`} strokeDashoffset={`${2 * Math.PI * 45 * (1 - pct / 100)}`}
                    strokeLinecap="round" style={{ transition: "stroke-dashoffset 1s linear" }} />}
                </svg>
                {s ? (
                  <div className="text-center">
                    <div className="font-mono text-4xl font-bold tabular">{done ? "00:00" : `${mm}:${ss}`}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">remaining</div>
                  </div>
                ) : (
                  <div className="text-center">
                    <Armchair size={40} className="mx-auto text-muted-foreground/40" />
                    <div className="mt-1 text-xs text-muted-foreground">{meta.label}</div>
                  </div>
                )}
              </div>

              {s ? (
                <div className="mt-5 text-center">
                  <div className="font-semibold">{s.customer_name}</div>
                  <div className="text-xs text-muted-foreground">{s.duration_minutes} min · {s.service_name}</div>
                  <div className="mt-1 text-[11px] text-muted-foreground">Staff: {s.staff_name}</div>
                </div>
              ) : (
                <div className="mt-5 text-center text-sm text-muted-foreground">Ready for the next guest</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
